import { Component, inject, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SharedStorageService } from '../../shared/services/shared-storage.service';
import { API_PARAMS } from '../../shared/constants/api.constants';
import { MatError } from '@angular/material/select';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import {
  API_DATE_FORMAT,
  DATE_FORMAT,
} from '../../shared/constants/date.constants';
import moment from 'moment/moment';
import { MatButton } from '@angular/material/button';
import { Router } from '@angular/router';
import { NgClass } from '@angular/common';
import { NumericOnlyDirective } from '../../directives/numeric-only.directive';
import { MatDialog } from '@angular/material/dialog';
import { SingleActionDialogComponent } from '../../shared/components/dialogs/single-action-dialog/single-action-dialog.component';
import { birthdayValidator, calculateAge } from '../../shared/utils/validator.util';

/**
 * 填寫個人基本資料
 */
@Component({
  selector: 'app-reserve-info',
  standalone: true,
  imports: [
    TranslatePipe,
    FormsModule,
    MatRadioGroup,
    MatRadioButton,
    ReactiveFormsModule,
    MatButton,
    MatError,
    NgClass,
    NumericOnlyDirective,
  ],
  templateUrl: './reserve-info.component.html',
  styleUrl: './reserve-info.component.scss',
})
export class ReserveInfoComponent implements OnInit {
  translateService: TranslateService = inject(TranslateService);
  sharedStorageService: SharedStorageService = inject(SharedStorageService);
  router: Router = inject(Router);
  dialog: MatDialog = inject(MatDialog);

  showError: boolean = false;

  // 生日三欄輸入（西元年 / 月 / 日）
  birthYear: string = '';
  birthMonth: string = '';
  birthDay: string = '';

  // 三欄都填完後，才即時顯示生日錯誤（做法 B）
  birthdayFilled: boolean = false;

  form = new FormGroup({
    gender: new FormControl<string>('', [Validators.required]),
    birthday: new FormControl<moment.Moment | null>(null, [
      Validators.required,
      (control: FormControl) =>
        birthdayValidator(control, this.translateService, {
          max: 75,
          min: 18,
        }, calculateAge),
    ]),
    status: new FormControl<string>(''),
    menopausal: new FormControl<string>(''),
  });

  ngOnInit() {
    // 將當前預約步驟設置為2
    setTimeout(() => {
      this.sharedStorageService.set('reserveStep', 2);
    });

    // 監聽gender變化時，添加/刪除女性相關欄位校驗
    this.form.get('gender')?.valueChanges.subscribe(value => {
      // 當 gender 變動時執行
      if (this.isFemale) {
        this.form.get('status')?.setValidators([Validators.required]);
        this.form.get('menopausal')?.setValidators([Validators.required]);
      } else {
        this.form.get('status')?.clearValidators();
        this.form.get('menopausal')?.clearValidators();
      }
      this.form.get('status')?.updateValueAndValidity();
      this.form.get('menopausal')?.updateValueAndValidity();
    });

    const userPlanRequest = this.sharedStorageService.get('userPlanRequest');
    const loginStatus = this.sharedStorageService.get('loginStatus');

    // 只有 memberLogin 才算有 web 帳號
    const hasWebAccount = loginStatus === 'memberLogin';
    const hasFilledReserveInfo = this.sharedStorageService.get('hasFilledReserveInfo');

    // 只有有 web 帳號或已填寫過時才預帶
    const shouldPreFill = hasFilledReserveInfo || hasWebAccount;

    // 性別預帶邏輯
    if (
      shouldPreFill &&
      userPlanRequest?.gender !== undefined &&
      userPlanRequest?.gender !== null &&
      userPlanRequest?.gender !== ''
    ) {
      this.form.get('gender')?.setValue(userPlanRequest?.gender);
    }
    // 生日預帶邏輯
    if (
      shouldPreFill &&
      userPlanRequest?.birthday !== undefined &&
      userPlanRequest?.birthday !== null &&
      userPlanRequest?.birthday !== ''
    ) {
      const preFillBirthday = moment(userPlanRequest?.birthday, API_DATE_FORMAT);
      if (preFillBirthday.isValid()) {
        this.form.get('birthday')?.setValue(preFillBirthday);
        // 同步帶入三欄輸入
        this.birthYear = preFillBirthday.format('YYYY');
        this.birthMonth = preFillBirthday.format('MM');
        this.birthDay = preFillBirthday.format('DD');
      }
    }
  }

  /**
   * 生日三欄（年/月/日）任一欄變動時，組出日期並寫回 birthday FormControl。
   * 維持 moment.Moment 型別，讓既有的驗證器、calculateAge、送出邏輯都不需更動。
   */
  onBirthdayPartChange(): void {
    const year = this.birthYear.trim();
    const month = this.birthMonth.trim();
    const day = this.birthDay.trim();
    const control = this.form.get('birthday');

    // 三欄都填完後才開始即時顯示錯誤
    this.birthdayFilled = !!year && !!month && !!day;

    // 三欄都還沒填完整前，維持 null（交給 required 驗證）
    if (!year || !month || !day) {
      control?.setValue(null);
      control?.markAsTouched();
      return;
    }

    // 補零成 YYYY/MM/DD，使用 moment 嚴格模式驗證（可正確判斷閏年、大小月）
    const dateStr = `${year.padStart(4, '0')}/${month.padStart(2, '0')}/${day.padStart(2, '0')}`;
    const parsed = moment(dateStr, DATE_FORMAT, true);

    // 無效日期（如 2/30、閏年外的 2/29）仍寫入 invalid moment，交給驗證器顯示格式錯誤
    control?.setValue(parsed.isValid() ? parsed : moment.invalid());
    control?.markAsTouched();
    control?.updateValueAndValidity();
  }

  /**
   * 生日欄位填滿時自動聚焦下一欄。
   * 年填滿 4 碼 → 跳月；月填滿 2 碼 → 跳日。
   * @param field 目前欄位（'year' | 'month'）
   * @param nextInput 下一欄的 input 元素
   */
  autoFocusNext(field: 'year' | 'month', nextInput: HTMLInputElement): void {
    const filled =
      field === 'year' ? this.birthYear.trim().length === 4 : this.birthMonth.trim().length === 2;
    if (filled && nextInput && !nextInput.disabled) {
      nextInput.focus();
    }
  }

  /** 三欄是否都為空 */
  get isBirthdayAllEmpty(): boolean {
    return !this.birthYear.trim() && !this.birthMonth.trim() && !this.birthDay.trim();
  }

  /** 送出後仍三欄皆空 → 需要三欄一起標紅、顯示年份提示 */
  get showBirthdayEmptyError(): boolean {
    return this.showError && this.isBirthdayAllEmpty;
  }

  /** 年份是否不合法（已填但非 4 碼、或超出 1900~今年） */
  get isYearInvalid(): boolean {
    const year = this.birthYear.trim();
    if (!year) return false;
    const y = Number(year);
    return year.length !== 4 || isNaN(y) || y < 1900 || y > moment().year();
  }

  /** 月份是否不合法（已填但不在 1~12） */
  get isMonthInvalid(): boolean {
    const month = this.birthMonth.trim();
    if (!month) return false;
    const m = Number(month);
    return isNaN(m) || m < 1 || m > 12;
  }

  /** 日期是否不合法（已填但不在 1~該年月天數；閏年、大小月由 moment 判斷） */
  get isDayInvalid(): boolean {
    const day = this.birthDay.trim();
    if (!day) return false;
    const d = Number(day);
    if (isNaN(d) || d < 1) return true;

    // 若年、月合法，用該年月的實際天數判斷；否則僅用上限 31 粗判
    if (!this.isYearInvalid && !this.isMonthInvalid && this.birthYear.trim() && this.birthMonth.trim()) {
      const daysInMonth = moment(
        `${this.birthYear.trim().padStart(4, '0')}-${this.birthMonth.trim().padStart(2, '0')}`,
        'YYYY-MM'
      ).daysInMonth();
      return d > daysInMonth;
    }
    return d > 31;
  }

  // 根據輸入的生日計算年齡
  get age(): string {
  const birthday = this.form.get('birthday')?.value;
  if (!birthday) return '--';  // 簡化：calculateAge 內會處理無效
  
  const ageNum = calculateAge(birthday);  // 直接呼叫，支援 string | moment.Moment
  return ageNum > 0 ? String(ageNum) : '--';
}

  get isFemale(): boolean {
    return this.form?.get('gender')?.value === API_PARAMS.GENDER.FEMALE;
  }

  // 獲取驗證生日錯誤訊息
  getBirthdayErrorMessage() {
    const control = this.form.get('birthday');
    // 送出後(showError) 或 三欄都填完(birthdayFilled) 才顯示錯誤
    if (!control || !control.errors || (!this.showError && !this.birthdayFilled)) return '';

    const errors = control.errors;

    // mat-datepickerParse錯誤
    if (errors['matDatepickerParse']) {
      return this.translateService.instant('error.birthday-error.format');
    }
    if (errors['required']) {
      return this.translateService.instant('error.birthday-error.required');
    }
    if (errors['invalidFormat']) {
      return errors['invalidFormat'];
    }
    if (errors['invalidAge']) {
      return ` <div class="input-calculator-msg input-mobile-msg">
          </div><div class="input-warn-msg">
        <img width="16" height="16" src="/assets/img/i-warn-outline.svg" alt=""/><span>${this.translateService.instant('error.birthday-error.reserve-age-invalid')}</span></div>`;
    }
    return '';
  }

  // 點擊上一步
  handlePreviousClick() {
    this.router.navigate(['/healthcheck/reserve/location']).then();
  }

  // 點擊下一步
  onSubmit() {
    this.showError = true;
    if (this.form.valid) {
      this.showError = false;

      // 若選擇女性且勾選懷孕，則顯示提示對話框並不能往下預約
      if (
          this.form.get('gender')?.value === API_PARAMS.GENDER.FEMALE &&
          this.form.get('status')?.value === API_PARAMS.FEMALE_STATUS.PREGNANCY
        ) {
        const femalePregnancyHintDialog = this.dialog.open(
          SingleActionDialogComponent,
          {
            data: {
              title: this.translateService.instant(
                'reserve-info.female-pregnancy-hint-title'
              ),
              message: this.translateService.instant(
                'reserve-info.female-pregnancy-hint-message'
              ),
              buttonText: this.translateService.instant('not-found.back-to-home'),
              showCloseButton: true,
              action: () => {
                femalePregnancyHintDialog.close();
                this.sharedStorageService.clear();
                this.router.navigate(['/healthcheck']).then();
              },
            },
          }
        );
        return;
      }

      const userPlanRequest = this.sharedStorageService.get('userPlanRequest');
      const businessPlanRequest = this.sharedStorageService.get('businessPlanRequest');
      if (userPlanRequest) {
        userPlanRequest.gender = this.form.get('gender')!.value!;
        userPlanRequest.birthday = this.form
          .get('birthday')!
          .value!.format(API_DATE_FORMAT)!;
        userPlanRequest.age = this.age;
        userPlanRequest.femaleStatus = false
        userPlanRequest.femaleMenopausal = false;
        if (this.isFemale) {
          userPlanRequest.femaleStatus =
            this.form.get('status')?.value === 'true';
          userPlanRequest.femaleMenopausal =
            this.form.get('menopausal')?.value === 'true';
        }
        this.sharedStorageService.set('userPlanRequest', userPlanRequest);
      }

      if (businessPlanRequest) {
        businessPlanRequest.gender = this.form.get('gender')!.value!;
        this.sharedStorageService.set('businessPlanRequest', businessPlanRequest);
      }

      this.sharedStorageService.set('hasFilledReserveInfo', true);

      this.router.navigate(['/healthcheck/reserve/personalhistory']).then();
    }
  }

  protected readonly API_PARAMS = API_PARAMS;
  protected readonly DATE_FORMAT = DATE_FORMAT;
}
